import { SupportedLocales } from '@/models/localization.model';
import { IPagination, IPaginationDTO } from './pagination.model';
import { MeasureUnitCode } from '@/modules/Products/helpers';

export interface ITranslateFieldResponse {
  data: {
    field: string;
    translated: string;
  };
}

export interface IGenerateNamePreviewResponse {
  data: Record<string, string>;
}

export enum ProductsEndpoints {
  PRODUCTS = '/products',
  TOP_PRODUCTS = '/top-products',
  STORES = '/stores',
  RESTORE_PRODUCTS = '/products/restore',
  PRODUCTS_TOGGLE_ACTIVE = '/products/toggle-active',
  MONTH_PRODUCTS = '/pages/monthly_product/products',
  PRODUCT_FILTERS_BY_CATEGORY = '/products/filters-by-category',
  PRODUCTS_STOCKS_BULK = '/products/stocks/bulk',
  PRODUCTS_REGENERATE_NAME = '/products/:id/regenerate-name',
}

export type ProductFieldSource = 'integration' | 'ai' | 'manual';

export type ProductFieldSources = {
  uktzed?: ProductFieldSource | null;
  weight?: ProductFieldSource | null;
  height?: ProductFieldSource | null;
  width?: ProductFieldSource | null;
  length?: ProductFieldSource | null;
  description?: Partial<Record<SupportedLocales, ProductFieldSource | null>>;
};

export interface IProductLocale {
  locale: SupportedLocales;
  name: string;
  slug: string;
  description: string;
  original_description?: string | null;
  original_name: string | null;
  seo_title: string | null;
  seo_keywords: string | null;
  seo_description: string | null;
  seo_text: string | null;
}

export interface IProductStore {
  id: string;
  title: string;
  slug: string;
  email: string;
  phone: string;
  logo: string;
}

export interface IProductCategory {
  id: string;
  parent_id: string | null;
  name: string;
  photo: string | null;
  slug: string;
  locale: IProductLocale[];
  parent_category: IProductCategory | null;
}

export interface IPhoto {
  name: string;
  file_name: string;
  uuid: string;
  preview_url: string;
  original_url: string;
  order: number;
  custom_properties: any[];
  extension: string;
  size: number;
}

export interface IPhotosOrder {
  id: string;
  order: number;
}

export interface IProduct {
  id: string;
  name: string | null;
  sku: string;
  external_id: string;
  slug: string;
  uktzed: string;
  imported: '0' | '1';
  is_custom_order: '0' | '1';
  weight: number;
  length: number;
  height: number;
  width: number;
  store: IProductStore;
  category: IProductCategory;
  active: boolean;
  photos: IPhoto[];
  locale: IProductLocale[];
  photo?: string;
  date_from?: string;
  date_to?: string;
  published?: boolean;
  unit_code?: MeasureUnitCode;
  package_quantity?: number;
  min_order_packages?: number;
  field_sources?: ProductFieldSources | null;
}

export interface IStoreItem {
  id: string;
  title: string;
  slug: string;
}

export interface IProductFilterValue {
  id: string;
  title: string;
  selected: boolean;
}

export enum IProductFilterType {
  MULTISELECT = 'multiselect',
  MULTISELECT_WITH_SEARCH = 'multiselect_with_search',
  RADIO_BUTTON = 'radio_button',
  SLIDER = 'slider',
}

export interface IProductSeoLocale {
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  seo_text: string;
}

export interface ITopProduct {
  id: string;
  position: number;
  product: IProduct;
  created_at: string;
}

// ----------------- DTOs ----------------

export interface IGetProductsOfMonthListDTO extends Partial<IPaginationDTO> {
  store_id?: string;
  category_id?: string;
  product_sku?: string;
  date_to?: string;
  date_from?: string;
  published?: '0' | '1';
  name?: string;
  limit?: number;
}

export interface IUpdateProductsOfMonthDTO {
  product_id: string;
  published: '0' | '1';
  date_to: string;
  date_from: string;
}

export interface IUpdateTopProductsDTO {
  product_id: string;
}

export interface IReorderTopProductsDTO {
  top_product_ids: string[];
}

export interface IGetProductsListDTO extends Partial<IPaginationDTO> {
  name?: string;
  sku?: string;
  search?: string;
  article?: string;
  external_id?: string;
  store?: string;
  partner_ids?: string[];
  category?: string;
  active?: '0' | '1';
  only_zero_price?: '0' | '1';
  only_without_uktzed?: '0' | '1';
  only_without_dimensions?: '0' | '1';
  limit?: number;
}

export interface IProductFilter {
  id: string;
  title: string;
  values: IProductFilterValue[];
  required: boolean;
  type: IProductFilterType;
  min?: string;
  max?: string;
  position?: number;
  updated_at?: string;
}

export interface IProductVariation {
  id: string;
  main?: boolean;
  name: string | null;
  sku: string;
  active: boolean;
  photo?: string | null;
  external_id?: string | null;
}

export interface IProductRegionsAndPricesListItem {
  id: string;
  product_id: string;
  region: string;
  available: boolean;
  quantity: number;
  price: string;
  discount_price: string;
}

export interface IDeleteBulkProductsDTO {
  ids?: string[];
  all?: boolean;
  sku?: string;
  name?: string;
  category_ids?: string[];
  active?: '0' | '1';
  partner_ids?: string[];
}

export interface IRestoreBulkProductDTO {
  ids?: string[];
  all?: boolean;
}

export interface IToggleActiveBulkProductDTO {
  ids?: string[];
  all?: boolean;
  sku?: string;
  name?: string;
  category_ids?: string[];
  active?: '0' | '1';
  partner_ids?: string[];
}

export interface IUpdateProductMainInfoDTO {
  locale: { [key in SupportedLocales]: { name: string; description: string; original_name?: string | null } };
  slug?: string;
  external_id?: string;
  photos?: File[];
  photo_ids?: string[];
  uktzed?: string;
  imported?: '0' | '1';
  weight?: number;
  length?: number;
  height?: number;
  width?: number;
  order: IPhotosOrder[];
  unit_code?: MeasureUnitCode;
  package_quantity?: number;
  min_order_packages?: number;
}

export interface ICreateProductMainInfoDTO extends IUpdateProductMainInfoDTO {
  store_id: string;
}

export interface IUpdateRegionsAndPricesItemDTO {
  available: boolean;
  price: number;
  discount_price?: number;
}

export interface IBulkUpdateStockItem {
  id: string;
  available: boolean;
  price: number;
  discount_price?: number;
}

export interface IBulkUpdateStocksDTO {
  stocks: IBulkUpdateStockItem[];
}

export interface IGetStoreListDTO {
  title?: string;
  limit?: number;
}

export interface IUpdateProductVariationDto {
  product_id: string;
  variation: IProductVariation;
}

export interface IBulkSearchProductVariationsDTO {
  articles: string[];
  page?: number;
  limit?: number;
}

export interface IAddProductVariationsBulkDTO {
  product_id: string;
  variation_ids: string[];
}

export interface IUpdateProductSeoDTO {
  locale: { [key in SupportedLocales]: IProductSeoLocale };
}

export interface IUpdateProductCharacteristicsDTO {
  category: string;
  filter_value_ids: string[];
}

export interface IUpdateProductDTO {
  id: string;
  data: IUpdateProductMainInfoDTO;
}

export interface ICreateProductDTO {
  mainInfoData: ICreateProductMainInfoDTO;
  filtersData: Record<string, string>;
}

// ------------ Responses --------------

export interface IProductListResponse {
  data: IProduct[];
  meta: IPagination;
}

export interface ITopProductListResponse {
  data: ITopProduct[];
}

export interface IGetProductResponse {
  data: IProduct;
}

export interface IGetTopProductResponse {
  data: ITopProduct;
}

export interface IUpdateProductResponse {
  data: IProduct;
}

export interface IGetFiltersByCategoryResponse {
  data: IProductFilter[];
}

export interface IGetProductVariationsResponse {
  data: IProductVariation[];
}

export interface IBulkSearchProductVariationsResponse {
  data: IProduct[];
  meta: IPagination;
  not_found: string[];
}

export interface IGetProductRegionsAndPricesResponse {
  data: IProductRegionsAndPricesListItem[];
}

export interface IUpdateRegionsAndPricesItemResponse {
  data: IProductRegionsAndPricesListItem;
}

export interface IBulkUpdateStocksResponse {
  data: { success: boolean };
}

export interface IStoreResponse {
  data: IStoreItem[];
}
